import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, googleProvider, db } from "../firebase";
import { trackProductEvent } from "../lib/analytics";
const functions = getFunctions();
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ================= SAVE USER TO FIRESTORE ================= */

  const syncUserToDB = async (firebaseUser) => {
    const ref = doc(db, "users", firebaseUser.uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      await setDoc(ref, {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        name: firebaseUser.displayName || "",
        photo: firebaseUser.photoURL || "",
        provider: firebaseUser.providerData[0]?.providerId || "password",

        // MVP future fields
        onboardingCompleted: false,
        role: "user",

        createdAt: serverTimestamp(),
      });
    }
  };

  /* ================= AUTH STATE ================= */

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        await syncUserToDB(firebaseUser);

        const u = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName,
          photo: firebaseUser.photoURL,
        };

        setUser(u);
        localStorage.setItem("skilll_user", JSON.stringify(u));
      } else {
        setUser(null);
        localStorage.removeItem("skilll_user");
      }

      setLoading(false);
    });

    return unsub;
  }, []);

  /* ================= ACTIONS ================= */

  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    void trackProductEvent(
      "login_success",
      { method: "password" },
      {
        user: {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
        },
      }
    );

    return userCredential;
  };

  const signup = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    void trackProductEvent(
      "signup_success",
      { method: "password" },
      {
        user: {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
        },
      }
    );

    return userCredential; // 🔥 REQUIRED
  };

  const loginWithGoogle = async () => {
    const res = await signInWithPopup(auth, googleProvider);
    await syncUserToDB(res.user);

    void trackProductEvent(
      "login_success",
      { method: "google" },
      {
        user: {
          uid: res.user.uid,
          email: res.user.email,
        },
      }
    );
  };

  const resetPassword = async (email) => {
    if (!email) {
      throw new Error("Email is required");
    }

    const sendResetPassword = httpsCallable(functions, "sendResetPassword");

    await sendResetPassword({ email });

    void trackProductEvent("password_reset_requested", { email });

    // ✅ bas success message dikhao
    // "If account exists, reset link sent"
  };

  const logout = async () => {
    const currentUser = auth.currentUser;

    void trackProductEvent(
      "logout",
      {},
      currentUser
        ? {
            user: {
              uid: currentUser.uid,
              email: currentUser.email,
            },
          }
        : {}
    );

    return signOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        loginWithGoogle,
        logout,
        resetPassword,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}
